/* eslint-disable max-lines */
import { PayloadAction } from '@reduxjs/toolkit';
import api from 'Cloud/Application/api';
import { MYOFFICE_VARIANTS } from 'Cloud/Application/Editor/MyOffice/myOffice.types';
import { sha1 } from 'hash-wasm';
import { logger } from 'lib/logger';
import { renderSharingNew } from 'reactApp//components/SharingNewBiz/SharingNew.helpers';
import { AccessStatus, EFrom } from 'reactApp//components/SharingNewBiz/SharingNew.types';
import { ShareAlbumAPICall } from 'reactApp/api/albums/ShareAlbumAPICall';
import { UnshareAlbumAPICall } from 'reactApp/api/albums/UnshareAlbumAPICall';
import { PublishFileApiCall } from 'reactApp/api/publish/PublishFileApiCall';
import { UnPublishFileApiCall } from 'reactApp/api/publish/UnPublishFileApiCall';
import { getEditorsFor } from 'reactApp/appHelpers/appHelpers';
import { IS_ONPREMISE } from 'reactApp/appHelpers/configHelpers';
import { toolbarActions } from 'reactApp/appHelpers/toolbarActions';
import { MAIL_ATTACHES_FOLDER_ID, ROOT_FOLDER_ID } from 'reactApp/constants/magicIdentificators';
import { AttachesItem } from 'reactApp/modules/attaches/attaches.types';
import { EditorItem } from 'reactApp/modules/editor/editor.types';
import { getFeatureByName, getFeatureWopi } from 'reactApp/modules/features/features.selectors';
import { isArchive, isFolder, isPreviewable, isVirusItem } from 'reactApp/modules/file/utils';
import { loadHomeFolder } from 'reactApp/modules/home/home.saga';
import { getHomeItemById } from 'reactApp/modules/home/home.selectors';
import {
    addFilesSuccess,
    cloneToCloudFailure,
    cloneToCloudSuccess,
    publishWeblink,
    resetWeblinkCountDownloads,
    toggleWeblinkAccessRights,
    toggleWeblinkDomestic,
    toggleWeblinkDownloadable,
    toggleWeblinkDownloadableRequest,
    unPublishWeblink,
    updateWeblinkAutoDelete,
    updateWeblinkAutoDeleteRequest,
    updateWeblinkCountDownloads,
    updateWeblinkExpires,
    updateWeblinkExpiresRequest,
} from 'reactApp/modules/modifying/modifying.actions';
import { publishErrors } from 'reactApp/modules/modifying/modifying.constants';
import { getIdParams } from 'reactApp/modules/modifying/modifying.helpers';
import {
    DeleteWeblinkExpiresRequest,
    IOpenPublishDialog,
    IPublishRequest,
    IUnPublishRequest,
    ToggleWeblinkDomesticRequest,
    ToggleWeblinkDownloadableRequest,
    ToggleWeblinkEditableRequest,
    ToggleWeblinkUploadRequest,
    UpdateWeblinkAutoDeleteRequest,
    UpdateWeblinkCountDownloadsRequest,
    UpdateWeblinkExpiresRequest,
} from 'reactApp/modules/modifying/modifying.types';
import { showVirusDlg } from 'reactApp/modules/popup/popup.module';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { getSearchRequestParams } from 'reactApp/modules/search/search.selectors';
import { SearchItem } from 'reactApp/modules/search/search.types';
import { showSnackbarAction } from 'reactApp/modules/snackbar/snackbar.actions';
import { SnackbarTypes } from 'reactApp/modules/snackbar/snackbar.types';
import {
    getStorageItemById,
    isDomainFolder,
    isMountedFolder,
    isMountedOrSharedFolder,
    isSharedFolder,
} from 'reactApp/modules/storage/storage.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { store } from 'reactApp/store';
import { AccessRights } from 'reactApp/types/Tree';
import { openDisabledFeaturePopupHelper } from 'reactApp/ui/DisabledFeatureDialog/DisabledFeatureDialog.helpers';
import { DisabledFeature } from 'reactApp/ui/DisabledFeatureDialog/DisabledFeatureDialog.types';
import { ActionName } from 'reactApp/ui/VirusDialog/VirusDialog.types';
import { callSagaFromAction } from 'reactApp/utils/callSagaFromAction';
import { isEditingAttach, isEditingStorage } from 'reactApp/utils/isEditingFile';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';
import { sendEditRadar } from 'reactApp/utils/sendEditRadar';
import { sendTmrGoal } from 'reactApp/utils/tmr';
import { all, call, cancel, put, select, take } from 'redux-saga/effects';

const CREATE_PUBLIC_ACTION_NAME = 'created-public';

const publishFileApiCall = (idParams) => new PublishFileApiCall().makeRequest(idParams);
const unPublishFileApiCall = ({ weblink }) => new UnPublishFileApiCall().makeRequest({ weblink });
const shareAlbumAPICall = ({ id }) => new ShareAlbumAPICall().makeRequest({ album_id: id });
export const unshareAlbumAPICall = ({ id }) => new UnshareAlbumAPICall().makeRequest({ album_id: id });

async function unpublishRequest(item) {
    if (!item.weblink) {
        return;
    }

    try {
        if (item.storage === EStorageType.albums) {
            await unshareAlbumAPICall({ id: item.id });
            return;
        }

        await unPublishFileApiCall({ weblink: item.weblink });

        if (IS_ONPREMISE) {
            store.dispatch(
                showSnackbarAction({
                    id: 'unpublish-success',
                    text: `Доступ к ${item.isFolder ? 'папке' : 'файлу'} «${item.name}» по ссылке закрыт`,
                    type: SnackbarTypes.success,
                    closable: true,
                })
            );
        }
    } catch ({ error }) {
        return { error };
    }
}

export function* toggleDownloadable(action: PayloadAction<ToggleWeblinkDownloadableRequest>) {
    const { item, isUnpublishRequest } = action.payload;

    if (item.weblinkDownloadable) {
        yield put(toggleWeblinkDownloadable({ id: item.id }));
        yield put(resetWeblinkCountDownloads({ id: item.id }));
    } else if (!isUnpublishRequest) {
        try {
            yield Promise.resolve(api.weblinks.downloadable({ weblink: item.weblink }));
            yield put(toggleWeblinkDownloadable({ id: item.id, downloadable: true }));
            yield put(toggleWeblinkAccessRights({ id: item.id }));
        } catch (error) {
            logger.error(error);

            yield put(
                showSnackbarAction({
                    closable: true,
                    id: 'toggle-weblink-downloadable-on-error',
                    type: SnackbarTypes.failure,
                    text: 'Не удалось разрешить загрузку',
                })
            );
        }
    }
}

export function* handleUnPublish({ items }: IUnPublishRequest) {
    const files = yield all(items.map((item) => unpublishRequest(item)));
    const { error } = files.find((item) => item?.error) || {};

    yield put(unPublishWeblink({ ids: items.map((item) => item.id) }));

    yield all(
        items.map((item) =>
            toggleDownloadable({ type: toggleWeblinkDownloadableRequest.toString(), payload: { item, isUnpublishRequest: true } })
        )
    );
    const storage = yield select(getCurrentStorage);

    if (storage === EStorageType.search) {
        const { xPageId, xReqId, query } = yield select(getSearchRequestParams);
        (items as SearchItem[]).forEach((item) =>
            sendPaymentGa({
                eventCategory: ECategoryGa.search,
                action: 'close-public',
                count_media: 0,
                have_faces: false,
                id_media: sha1(item?.id || ''),
                id_public: item?.home ? sha1(item?.home) : item?.id,
                is_archive: isArchive(item),
                is_edit: Boolean(getEditorsFor(item)?.length),
                is_full_render: isPreviewable(item),
                section: 'search',
                search_phrase: query,
                name_files: item?.name,
                type_content: item?.kind,
                page_id: xPageId,
                req_id: xReqId,
                pos: item?.pos,
                hash: item?.isFolder ? undefined : item?.hash,
                size: item?.isFolder ? undefined : item?.size,
            })
        );
    }

    if (!error) {
        return;
    }

    yield put(
        showSnackbarAction({
            id: 'unpublish-error',
            text: 'Не удалось убрать ссылку',
            type: SnackbarTypes.failure,
            closable: true,
        })
    );
}

function* sendPublicCreatedDwh(publishedItem, itemStorage, publishFrom) {
    if (publishedItem.weblink) {
        return;
    }

    sendTmrGoal({ goal: 'publish' });

    const currentStorage = yield select(getCurrentStorage);
    const { xPageId, xReqId, query } = yield select(getSearchRequestParams);
    const storage = itemStorage || currentStorage;
    const item = yield select((state) => getStorageItemById(state, storage, publishedItem.id));

    const isWopiFeatureEnabled = yield select(getFeatureWopi);
    if (item && isEditingStorage(currentStorage)) {
        sendEditRadar({
            i: 'new-public',
            storage: currentStorage,
            item: item as EditorItem,
            version: isWopiFeatureEnabled ? MYOFFICE_VARIANTS.wopi : MYOFFICE_VARIANTS.amr,
            isAttach: isEditingAttach(currentStorage),
            dwh: {
                id_public: (item as EditorItem).weblink,
            },
        });
    }

    if (!publishFrom) {
        return;
    }

    sendPaymentGa({
        eventCategory: ECategoryGa.public,
        action: CREATE_PUBLIC_ACTION_NAME,
        public_id: item.weblink,
        type_object: item.kind,
        extension: 'ext' in item ? item.ext : '',
        type_for: item.weblinkDomestic ? 'domestic' : 'external',
        type_access: item.weblinkAccessRights || 'r',
        time_ttl: item.weblinkExpires === 0 ? 'unlimited' : 'time',
        source: publishFrom,
    });

    if (storage === EStorageType.search) {
        sendPaymentGa({
            eventCategory: ECategoryGa.publicSearch,
            action: CREATE_PUBLIC_ACTION_NAME,
            public_id: item.weblink,
            type_object: item.kind,
            extension: 'ext' in item ? item.ext : '',
            type_for: item.weblinkDomestic ? 'domestic' : 'external',
            type_access: item.weblinkAccessRights || 'r',
            time_ttl: item.weblinkExpires === 0 ? 'unlimited' : 'time',
            source: publishFrom,
            section: storage,
            search_phrase: query,
            name_files: item.nameWithoutExt,
            type_content: item.kind,
            page_id: xPageId,
            req_id: xReqId,
            pos: item.pos,
            hash: item?.hash || '',
            size: item.size,
        });
    }
}

export function* publishAlbum({ item, itemStorage = EStorageType.albums, publishFrom = 'album' }: IPublishRequest) {
    const { data: weblink } = yield call(shareAlbumAPICall, { id: item.id });

    yield sendPublicCreatedDwh(item, itemStorage, publishFrom);

    yield put(publishWeblink({ weblink: weblink.id, id: item.id }));
}

export function* updateExpires(action: PayloadAction<UpdateWeblinkExpiresRequest>) {
    const { item, expires } = action.payload;
    try {
        yield Promise.resolve(
            api.weblinks.expires({
                weblink: item.weblink,
                expires: Math.trunc(expires / 1000),
            })
        );
        yield put(updateWeblinkExpires({ id: item.id, expires }));
    } catch (error) {
        yield cancel();
    }
}

export function* deleteExpires(action: PayloadAction<DeleteWeblinkExpiresRequest>) {
    const { item } = action.payload;
    try {
        yield Promise.resolve(
            api.weblinks.expires({
                weblink: item.weblink,
            })
        );
        yield put(updateWeblinkExpires({ id: item.id, expires: 0 }));
        yield put(updateWeblinkAutoDeleteRequest({ item, expires: 0 }));
    } catch (error) {
        yield cancel();
    }
}

function* toggleDomesticRequest({ item: publishItem }: ToggleWeblinkDomesticRequest) {
    const currentStorage = yield select(getCurrentStorage);
    const storage = publishItem.storage || currentStorage;

    const item = yield select((state) => getStorageItemById(state, storage, publishItem.id));

    try {
        if (item.weblinkDomestic) {
            yield Promise.resolve(
                api.weblinks.domestic.off({
                    weblink: item.weblink,
                })
            );
        } else {
            yield Promise.resolve(
                api.weblinks.domestic.on({
                    weblink: item.weblink,
                })
            );
        }

        yield put(toggleWeblinkDomestic({ id: item.id, domestic: !item.weblinkDomestic }));
    } catch (error) {
        logger.error(error);
        yield cancel();
    }
}

export function* updateAutoDelete(action: PayloadAction<UpdateWeblinkAutoDeleteRequest>) {
    const { item, expires } = action.payload;

    try {
        if (expires) {
            yield updateExpires(updateWeblinkExpiresRequest({ item, expires }));

            yield Promise.resolve(
                api.weblinks.autodelete.on({
                    weblink: item.weblink,
                })
            );
        } else {
            yield Promise.resolve(
                api.weblinks.autodelete.off({
                    weblink: item.weblink,
                })
            );
        }

        yield put(updateWeblinkAutoDelete({ id: item.id, autoDelete: Boolean(expires) }));
    } catch (error) {
        logger.error(error);
        yield cancel();
    }
}

export function* toggleUploadRequest({ item: publishItem, id }: ToggleWeblinkUploadRequest) {
    const currentStorage = yield select(getCurrentStorage);
    const storage = publishItem.storage || currentStorage;

    const item = yield select((state) => getStorageItemById(state, storage, publishItem.id));

    if ((id && id === AccessStatus.ReadOnly) || item.weblinkAccessRights === AccessRights.rw) {
        try {
            yield Promise.resolve(api.weblinks.readonly({ weblink: item.weblink }));
            yield put(toggleWeblinkAccessRights({ id: item.id, type: AccessRights.r }));
        } catch (error) {
            yield put(
                showSnackbarAction({
                    closable: true,
                    id: 'toggle-weblink-readonly-error',
                    type: SnackbarTypes.failure,
                    text: 'Не удалось запретить загрузку в папку',
                })
            );
        }
    } else {
        try {
            yield Promise.resolve(api.weblinks.writable({ weblink: item.weblink }));
            yield put(toggleWeblinkAccessRights({ id: item.id, type: AccessRights.rw }));
        } catch (error) {
            logger.error(error);

            yield put(
                showSnackbarAction({
                    closable: true,
                    id: 'toggle-weblink-writable-error',
                    type: SnackbarTypes.failure,
                    text: 'Не удалось разрешить загрузку в папку',
                })
            );
        }
    }
}

export function* toggleEditable(action: PayloadAction<ToggleWeblinkEditableRequest>) {
    const { item, id } = action.payload;

    if ((id && id === AccessStatus.ReadOnly) || item.weblinkAccessRights === AccessRights.rw) {
        try {
            yield Promise.resolve(api.weblinks.editable.off({ weblink: item.weblink }));
            yield put(toggleWeblinkAccessRights({ id: item.id, type: AccessRights.r }));
        } catch (error) {
            logger.error(error);

            yield put(
                showSnackbarAction({
                    closable: true,
                    id: 'toggle-weblink-editable-off-error',
                    type: SnackbarTypes.failure,
                    text: 'Не удалось запретить совместное редактирование',
                })
            );
        }
    } else {
        try {
            yield Promise.resolve(api.weblinks.editable.on({ weblink: item.weblink }));
            yield put(toggleWeblinkAccessRights({ id: item.id, type: AccessRights.rw }));
        } catch (error) {
            logger.error(error);

            yield put(
                showSnackbarAction({
                    closable: true,
                    id: 'toggle-weblink-editable-on-error',
                    type: SnackbarTypes.failure,
                    text: 'Не удалось разрешить совместное редактирование',
                })
            );
        }
    }
}

export function* updateCount(action: PayloadAction<UpdateWeblinkCountDownloadsRequest>) {
    const { item, count_downloads } = action.payload;

    try {
        yield Promise.resolve(api.weblinks.count_downloads({ weblink: item.weblink, count_downloads }));

        if (count_downloads === 0) {
            yield put(resetWeblinkCountDownloads({ id: item.id }));
        } else {
            yield put(updateWeblinkCountDownloads({ id: item.id, count_downloads }));
        }
    } catch (error) {
        logger.error(error);

        yield put(
            showSnackbarAction({
                closable: true,
                id: 'toggle-weblink-downloadable-off-error',
                type: SnackbarTypes.failure,
                text: 'Не удалось обновить счетчик загрузок',
            })
        );
    }
}

export function* publishRequest({ item: publishItem, itemStorage, publishFrom }: IPublishRequest) {
    const currentStorage = yield select(getCurrentStorage);
    const storage = publishItem.storage || itemStorage || (currentStorage as EStorageType);
    const isAlbum = storage === EStorageType.albums;
    const isDomesticFeatureEnabled = yield select((state) => getFeatureByName(state, 'public-domestic-by-default'));
    const item = yield select((state) => getStorageItemById(state, storage, publishItem.id));

    if (!item) {
        return;
    }

    const idParams = getIdParams(storage, item.id);

    const domesticByDefault = !item?.weblink && !item?.weblinkDomestic && isDomesticFeatureEnabled;

    if (item.weblink) {
        return;
    }

    try {
        if (isAlbum) {
            yield call(publishAlbum, { item, itemStorage, publishFrom });
            return;
        }

        const { data } = yield call(publishFileApiCall, idParams);

        yield sendPublicCreatedDwh(item, storage, publishFrom);

        yield put(publishWeblink({ id: item.id, weblink: data.body, item }));

        if (domesticByDefault) {
            yield toggleDomesticRequest({ item });
        }
    } catch (error) {
        logger.error(error);

        yield put(
            showSnackbarAction({
                id: 'error',
                text: publishErrors.getMessage(error),
                type: SnackbarTypes.failure,
                closable: true,
            })
        );
    }
}

export function* handlePublishRequest(action: PayloadAction<IPublishRequest>) {
    const { item, itemStorage } = action.payload;

    yield publishRequest({ item, itemStorage });
}

export function* prepareHomeForAttachesClone(item: AttachesItem, storage: EStorageType) {
    yield loadHomeFolder({ id: ROOT_FOLDER_ID, isFolder: true });

    const attachFolder: ReturnType<typeof getHomeItemById> = yield select(getHomeItemById, MAIL_ATTACHES_FOLDER_ID);
    if (attachFolder) {
        yield loadHomeFolder({ id: MAIL_ATTACHES_FOLDER_ID, isFolder: true });
    }

    const homeItemId = item.home ?? `${MAIL_ATTACHES_FOLDER_ID}/${item.name}`;
    let newItem = (yield select(getHomeItemById, homeItemId)) as ReturnType<typeof getHomeItemById>;

    if (!newItem || newItem.size !== item.size) {
        toolbarActions.clone({
            id: item.id,
            source: 'attaches-autoclone',
            destination: MAIL_ATTACHES_FOLDER_ID,
            autoClone: true,
            storage,
        });

        let action = yield take([addFilesSuccess, cloneToCloudFailure]);

        if (action.type !== addFilesSuccess.toString()) {
            return;
        }

        if (isFolder(action.payload?.items?.[0])) {
            action = yield take(addFilesSuccess);
        }

        if (storage === EStorageType.stock) {
            // Обновляем дерево папок после клонирования нового файла)
            yield loadHomeFolder({ id: MAIL_ATTACHES_FOLDER_ID, isFolder: true, force: true });
        }

        newItem = action.payload?.items?.[0];
    } else if (!item.home) {
        yield put(cloneToCloudSuccess({ idx: [item.id], home: [homeItemId], storageFrom: storage }));
    }

    return newItem;
}

export function* openPublishDialogSaga(data: IOpenPublishDialog) {
    const {
        item,
        publishFrom,
        autoPublish = true,
        defaultAccessRights: accessRights,
        gaSuffix,
        onClose,
        itemStorage,
        from = EFrom.WEBLINK,
    } = data;

    const defaultAccessRights = accessRights === AccessStatus.Upload && item.weblinkAccessRights !== AccessRights.rw;
    const { isFull: isOverquota } = yield select(UserSelectors.getCloudSpace);
    const isB2B = yield select(UserSelectors.isB2BUser) || IS_ONPREMISE;
    const isFromPublish = from === EFrom.WEBLINK;

    let storage = item.storage || itemStorage;
    let newItem = item;

    if (isVirusItem(item)) {
        yield put(showVirusDlg({ items: [item], actionName: ActionName.publish, doNotChangeRoute: true }));
        return;
    }

    if (isOverquota && !isB2B) {
        openDisabledFeaturePopupHelper({ disabledFeature: DisabledFeature.publish });
        return;
    }

    if (!isFromPublish) {
        const { isShared }: ReturnType<typeof isMountedOrSharedFolder> = yield select(isMountedOrSharedFolder, item);
        if (isDomainFolder(item) || (item.isMounted && !isMountedFolder(item)) || (isShared && !isSharedFolder(item))) {
            // Если папка внутри примонтированной, то не показываем (нельзя шарить)
            // Если же это сама примонтированная папка, то показываем (там кнопка Отказаться)
            return;
        }

        yield call(renderSharingNew, {
            id: item.id,
            itemStorage: storage,
            from,
        });

        return;
    }

    if (storage === EStorageType.attaches || storage === EStorageType.viewerAttaches || item.storage === EStorageType.attaches) {
        newItem = yield prepareHomeForAttachesClone(item, storage as EStorageType);

        if (!newItem) {
            return;
        }

        storage = EStorageType.home;
    }

    if (autoPublish) {
        yield publishRequest({ item: newItem, itemStorage: storage, publishFrom });
    }

    if (defaultAccessRights) {
        yield toggleUploadRequest({ item: newItem });
    }

    yield call(renderSharingNew, {
        id: newItem.id,
        itemStorage: storage,
        from,
        gaSuffix,
        onClose,
    });
}
export const handleUnPublishRequest = (payload: PayloadAction<IUnPublishRequest>) =>
    callSagaFromAction<IUnPublishRequest>(handleUnPublish, payload);

export const toggleDomestic = (payload: PayloadAction<ToggleWeblinkDomesticRequest>) =>
    callSagaFromAction<ToggleWeblinkDomesticRequest>(toggleDomesticRequest, payload);

export const handleOpenPublishDialog = (payload: PayloadAction<IOpenPublishDialog>) =>
    callSagaFromAction<IOpenPublishDialog>(openPublishDialogSaga, payload);

export const toggleUpload = (payload: PayloadAction<ToggleWeblinkUploadRequest>) =>
    callSagaFromAction<ToggleWeblinkUploadRequest>(toggleUploadRequest, payload);
